import React, { useMemo, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { LogoLockup } from "@/components/Logo";
import { ArrowLeft, Eye, EyeOff, ShieldCheck } from "lucide-react";

export default function ResetPassword() {
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const navigate = useNavigate();

  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [show, setShow] = useState(false);
  const [loading, setLoading] = useState(false);

  const strength = useMemo(() => {
    let score = 0;
    if (password.length >= 8) score += 1;
    if (/[A-Z]/.test(password)) score += 1;
    if (/[0-9]/.test(password)) score += 1;
    if (/[^A-Za-z0-9]/.test(password)) score += 1;
    if (password.length >= 12) score += 1;
    return score; // 0–5
  }, [password]);

  const strengthLabel = ["Too weak", "Weak", "Okay", "Good", "Strong", "Excellent"][strength];
  const strengthColor = ["bg-slate-200", "bg-rose-400", "bg-orange-400", "bg-amber-400", "bg-emerald-400", "bg-emerald-600"][strength];

  const submit = async (e) => {
    e.preventDefault();
    if (password.length < 8) return toast.error("Use at least 8 characters.");
    if (password !== confirm) return toast.error("Passwords don't match.");
    setLoading(true);
    try {
      await api.post("/auth/reset-password", { token, new_password: password });
      toast.success("Password reset — please sign in.");
      navigate("/login");
    } catch (err) {
      const detail = err.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Reset failed. Request a new link.");
    } finally { setLoading(false); }
  };

  if (!token) {
    return (
      <div className="min-h-screen grid place-items-center bg-[#FBF5EA] p-6" data-testid="reset-missing-token">
        <div className="max-w-md w-full bg-white border border-slate-200 rounded-2xl p-8 text-center shadow-sm">
          <LogoLockup size={40} />
          <h1 className="mt-6 font-display font-black text-2xl tracking-tighter">Missing reset token</h1>
          <p className="mt-3 text-slate-600 text-sm">This page needs a valid reset link. Head back to Forgot Password to request a new one.</p>
          <Link to="/forgot-password">
            <Button className="mt-6 rounded-full bg-orange-800 hover:bg-orange-900 text-white h-11 px-6">Request a new link</Button>
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-stretch bg-[#FBF5EA]" data-testid="reset-password-page">
      <div className="flex-1 flex items-center justify-center p-6 sm:p-10">
        <div className="max-w-md w-full">
          <Link to="/login" className="inline-flex items-center gap-1 text-xs text-slate-500 hover:text-slate-800 mb-6">
            <ArrowLeft size={14} /> Back to sign in
          </Link>
          <LogoLockup size={44} />
          <h1 className="mt-8 font-display font-black text-4xl sm:text-5xl tracking-tighter">Set a new password</h1>
          <p className="mt-3 text-slate-600 text-sm">Choose a strong one you'll remember. Minimum 8 characters.</p>

          <form onSubmit={submit} className="mt-8 space-y-5">
            <div>
              <Label htmlFor="pw" className="text-xs tracking-widest uppercase font-bold">New password</Label>
              <div className="relative">
                <Input
                  data-testid="reset-password-input"
                  id="pw"
                  type={show ? "text" : "password"}
                  required
                  autoFocus
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="mt-1 h-12 pr-11"
                />
                <button
                  type="button"
                  onClick={() => setShow((v) => !v)}
                  aria-label={show ? "Hide password" : "Show password"}
                  className="absolute right-3 top-1/2 -translate-y-1/2 mt-0.5 text-slate-500 hover:text-slate-900"
                >
                  {show ? <EyeOff size={16} /> : <Eye size={16} />}
                </button>
              </div>

              {password.length > 0 && (
                <div className="mt-2">
                  <div className="h-1.5 bg-slate-100 rounded-full overflow-hidden">
                    <div className={`h-full ${strengthColor} transition-all`} style={{ width: `${(strength / 5) * 100}%` }} />
                  </div>
                  <div className="text-[11px] text-slate-500 mt-1">{strengthLabel}</div>
                </div>
              )}
            </div>

            <div>
              <Label htmlFor="pw2" className="text-xs tracking-widest uppercase font-bold">Confirm new password</Label>
              <Input
                data-testid="reset-confirm-input"
                id="pw2"
                type={show ? "text" : "password"}
                required
                value={confirm}
                onChange={(e) => setConfirm(e.target.value)}
                className="mt-1 h-12"
              />
              {confirm.length > 0 && confirm !== password && (
                <div className="text-[11px] text-rose-600 mt-1">Passwords don't match yet.</div>
              )}
            </div>

            <Button
              data-testid="reset-submit"
              type="submit"
              disabled={loading || password.length < 8 || password !== confirm}
              className="w-full h-12 rounded-full bg-orange-800 hover:bg-orange-900 text-white active:scale-95 transition-transform gap-2"
            >
              <ShieldCheck size={16} /> {loading ? "Updating…" : "Update password"}
            </Button>
          </form>

          <p className="mt-6 text-[11px] text-slate-500">
            After you reset, you'll be sent to the sign-in screen. All other devices where you were logged in stay signed in until their token expires.
          </p>
        </div>
      </div>

      <div className="hidden lg:block flex-1 bg-orange-800 relative overflow-hidden">
        <img src="https://images.unsplash.com/photo-1783254877023-f27a32c7e8bb?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzOTB8MHwxfHNlYXJjaHwyfHxpbmRpYW4lMjBzdHVkZW50JTIwc3R1ZHlpbmclMjBob3N0ZWx8ZW58MHx8fHwxNzgzNjU5OTc2fDA&ixlib=rb-4.1.0&q=85" className="w-full h-full object-cover opacity-70" alt="" />
        <div className="absolute inset-0 bg-orange-900/40" />
        <div className="absolute bottom-16 left-16 right-16 text-white">
          <div className="text-xs tracking-[0.25em] uppercase font-bold text-amber-400">Almost done</div>
          <div className="font-display font-black text-4xl tracking-tighter mt-2">Set a new password and you're back in.</div>
        </div>
      </div>
    </div>
  );
}
